import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartPlus,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { productService, categoryService } from 'services';
import { _Object } from 'utils/interfaces';
import { doAmountFormat, routes } from 'utils';
import { addToCart } from 'redux/slices/cart.slice';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';

// Helper to extract image URL safely
export const getProductImage = (item: any): string => {
  const img =
    item?.image ||
    item?.thumbnail ||
    item?.image_url ||
    item?.picture ||
    item?.featured_image;
  return typeof img === 'string' ? img.trim() : '';
};

// Helper to generate initials from product name
export const getProductInitials = (item: any) => {
  if (item?.initials) return item.initials;
  const name = (item?.name || '').trim();
  if (!name) return 'PR';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Helper to build normalized cart item object
export const buildCartProduct = (product: any) => {
  const salePrice = product.discounted_price
    ? product.discounted_price
    : product.regular_price || product.price || 0;
  const comparePrice = product.regular_price || product.price || 0;
  const productId = product.id ?? product._id;
  const productImage = getProductImage(product);
  const initials = getProductInitials(product);

  return {
    ...product,
    id: productId,
    name: product.name,
    sku: product.sku,
    price: salePrice,
    regular_price: comparePrice,
    tax_rate: product.tax?.rate || product.tax_rate || 0,
    tax: product.tax || (product.tax_rate ? { rate: product.tax_rate } : null),
    brand_name: product?.brand?.name || product?.brand_name || '',
    tax_rate_id: product.tax_rate_id || null,
    description: product.description || '',
    stock: product.stock_status || 'instock',
    quantity: 1,
    image: productImage || '',
    initials: initials,
  };
};

export const isSameProduct = (p1: any, p2: any) => {
  if (!p1 || !p2) return false;
  const id1 = p1.id ?? p1._id;
  const id2 = p2.id ?? p2._id;
  return id1 !== undefined && id1 !== null && id1 === id2;
};

// Sub-component for individual product card
const ProductCard = ({
  product,
  isSelected,
  onSelect,
  onAddToCart,
}: {
  product: any;
  isSelected?: boolean;
  onSelect: (product: any) => void;
  onAddToCart: (product: any) => void;
}) => {
  const [imgError, setImgError] = useState(false);
  const initials = getProductInitials(product);
  const productImage = getProductImage(product);

  const salePrice = product.discounted_price
    ? product.discounted_price
    : product.regular_price || product.price || 0;
  const comparePrice = product.regular_price || product.price || 0;
  const hasDiscount = Boolean(
    product.discounted_price &&
    product.regular_price &&
    product.discounted_price < product.regular_price,
  );
  const stock =
    product.stock ||
    product.quantity ||
    (product.stock_status === 'instock'
      ? 'In stock'
      : product.stock_status || 0);

  return (
    <div
      className={`pos-product-card d-flex flex-column bg-white border rounded ${
        isSelected ? 'pos-product-selected border-primary shadow-sm' : 'border-light-subtle'
      }`}
      onClick={() => onSelect(product)}
      style={{ cursor: 'pointer' }}
    >
      <div className="pos-card-thumbnail d-flex align-items-center justify-content-center overflow-hidden mb-2">
        {productImage && !imgError ? (
          <img
            src={productImage}
            alt={product.name}
            className="pos-card-img rounded"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="pos-card-avatar rounded d-flex align-items-center justify-content-center fw-semibold">
            {initials}
          </div>
        )}
      </div>

      <div className="pos-card-content d-flex flex-column flex-grow-1 p-2">
        <h6 className="pos-card-title mb-1" title={product.name}>
          {product.name}
        </h6>

        <div className="pos-card-sku text-muted small mb-2 d-flex justify-content-between align-items-center">
          <span>SKU: {product.sku || 'N/A'}</span>
          <span>Stock: {stock}</span>
        </div>

        <div className="pos-card-footer d-flex align-items-center justify-content-between mt-auto">
          <div className="pos-card-price-box d-flex align-items-baseline gap-1">
            {hasDiscount && (
              <del className="pos-card-compare-price text-muted small">
                {doAmountFormat(comparePrice)}
              </del>
            )}

            <span className="pos-card-sale-price fw-bold text-dark">
              {doAmountFormat(salePrice)}
            </span>
          </div>

          <button
            type="button"
            className="pos-card-cart-btn btn btn-sm p-0 border-0"
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product);
            }}
            title="Add to cart (F3)"
            aria-label="Add to cart"
          >
            <FontAwesomeIcon icon={faCartPlus} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-component for frequently sold chip
const FrequentlySoldChip = ({
  item,
  isSelected,
  onSelect,
  onAddToCart,
}: {
  item: any;
  isSelected?: boolean;
  onSelect: (item: any) => void;
  onAddToCart: (item: any) => void;
}) => {
  const [imgError, setImgError] = useState(false);
  const itemImage = getProductImage(item);
  const initials = getProductInitials(item);
  const salePrice = item.discounted_price
    ? item.discounted_price
    : item.regular_price || item.price || 0;
  const comparePrice = item.regular_price || item.price || 0;
  const hasDiscount = Boolean(
    item.discounted_price &&
    item.regular_price &&
    item.discounted_price < item.regular_price,
  );

  return (
    <div
      className={`pos-freq-chip d-flex align-items-center gap-2 bg-white border rounded p-2 flex-shrink-0 cursor-pointer ${
        isSelected ? 'pos-freq-selected border-primary shadow-sm' : 'border-light-subtle'
      }`}
      onClick={() => onSelect(item)}
      role="button"
      tabIndex={0}
      title={item.name}
    >
      <div className="pos-freq-avatar-box d-flex align-items-center justify-content-center rounded flex-shrink-0">
        {itemImage && !imgError ? (
          <img
            src={itemImage}
            alt={item.name}
            className="pos-freq-img rounded"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="pos-freq-avatar rounded d-flex align-items-center justify-content-center fw-semibold">
            {initials}
          </span>
        )}
      </div>
      <div className="pos-freq-info d-flex flex-column justify-content-center flex-grow-1 overflow-hidden">
        <span className="pos-freq-name fw-semibold text-dark text-truncate small">
          {item.name}
        </span>
        <div className="pos-freq-footer d-flex align-items-center justify-content-between gap-1 mt-1">
          <div className="pos-freq-price-box d-flex align-items-baseline gap-1">
            {hasDiscount && (
              <del className="pos-freq-compare-price text-muted small">
                {doAmountFormat(comparePrice)}
              </del>
            )}
            <span className="pos-freq-sale-price fw-bold text-dark small">
              {doAmountFormat(salePrice)}
            </span>
          </div>

          <button
            type="button"
            className="pos-freq-cart-btn btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(item);
            }}
            title="Add to cart (F3)"
            aria-label="Add to cart"
          >
            <FontAwesomeIcon icon={faCartPlus} />
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProductCatalogProps {
  searchQuery?: string;
}

const ProductCatalog: React.FC<ProductCatalogProps> = ({
  searchQuery = '',
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  const handleAddToCart = (product: any) => {
    if (!product) return;
    if (product.stock_status === 'outofstock') {
      toast.error('This product is currently out of stock', { autoClose: 3000 });
      return;
    }
    const cartItem = buildCartProduct(product);
    dispatch(addToCart(cartItem));
    setSelectedProduct(product);
    toast.success(`${product.name} added to cart`, { autoClose: 1500 });
  };

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);
  };

  // Keyboard shortcuts: F2 to edit selected product, F3 to add selected product to cart
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        const productId = selectedProduct?.id || selectedProduct?._id;
        if (productId) {
          navigate(`${routes.products}/edit/${productId}`);
        } else {
          toast.warning('Please select a product first');
        }
      } else if (e.key === 'F3') {
        e.preventDefault();
        if (selectedProduct) {
          handleAddToCart(selectedProduct);
        } else {
          toast.warning('Please select a product first');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProduct, navigate]);

  const [categories, setCategories] = useState<any[]>([
    { id: 'all', name: 'All' },
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [productsList, setProductsList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreDropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 15;

  // Max visible category pills before collapsing into More dropdown
  const MAX_VISIBLE_CATEGORIES = 10;
  const visibleCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const overflowCategories = categories.slice(MAX_VISIBLE_CATEGORIES);

  const isOverflowSelected = overflowCategories.some(
    (cat) => selectedCategory === (cat.id || cat.name),
  );
  const selectedOverflowCategory = overflowCategories.find(
    (cat) => selectedCategory === (cat.id || cat.name),
  );

  // Close More dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreDropdownRef.current &&
        !moreDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreOpen]);

  const [topSellingProducts, setTopSellingProducts] = useState<any[]>([]);

  useEffect(() => {
    loadCategories();
    loadProducts();
    loadTopSelling();
  }, []);

  // Reset page when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const loadCategories = async () => {
    try {
      const data: _Object = await categoryService.getCategories({
        per_page: 50,
      });
      if (data?.items && data.items.length > 0) {
        setCategories([{ id: 'all', name: 'All' }, ...data.items]);
      }
    } catch {
      // ignore
    }
  };

  const loadTopSelling = async () => {
    try {
      const data: _Object = await productService.getProducts({
        top_selling: true,
        per_page: 20,
        filters: { is_sale_enabled: true },
      });
      if (data?.items && data.items.length > 0) {
        setTopSellingProducts(data.items);
      }
    } catch {
      // fallback
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data: _Object = await productService.getProducts({
        per_page: 999,
        filters: { status: 'published', is_sale_enabled: true },
      });
      if (data?.items && data.items.length > 0) {
        setProductsList(data.items);
      } else {
        setProductsList([]);
      }
    } catch {
      setProductsList([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter products by selected category and live search query
  const query = (searchQuery || '').trim().toLowerCase();
  const filteredProducts = productsList.filter((item) => {
    const matchesCategory =
      selectedCategory === 'all' ||
      item.category === selectedCategory ||
      item.category_id === selectedCategory ||
      (item.category_ids &&
        Array.isArray(item.category_ids) &&
        item.category_ids.includes(selectedCategory)) ||
      (item.categories &&
        Array.isArray(item.categories) &&
        item.categories.some(
          (c: any) =>
            c.id === selectedCategory ||
            c.name === selectedCategory ||
            c._id === selectedCategory,
        )) ||
      (item.category &&
        typeof item.category === 'object' &&
        (item.category.id === selectedCategory ||
          item.category._id === selectedCategory ||
          item.category.name === selectedCategory));

    if (!matchesCategory) return false;

    if (!query) return true;

    const nameMatch = (item.name || '').toLowerCase().includes(query);
    const skuMatch = (item.sku || '').toLowerCase().includes(query);
    const categoryMatch =
      typeof item.category === 'string' &&
      item.category.toLowerCase().includes(query);
    const brandMatch = (item?.brand?.name || item?.brand_name || '')
      .toLowerCase()
      .includes(query);

    return nameMatch || skuMatch || categoryMatch || brandMatch;
  });

  // Calculate total pages
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage),
  );

  // Current page products slice
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Generate pagination buttons
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        '...',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages,
    ];
  };

  return (
    <div className="pos-catalog-container d-flex flex-column flex-grow-1 h-100 overflow-hidden mt-2">
      {categories.length > 1 && (
        <div className="pos-category-bar flex-shrink-0 d-flex align-items-center gap-2 mb-3 flex-nowrap">
          {visibleCategories.map((cat) => {
            const isActive = selectedCategory === (cat.id || cat.name);
            return (
              <button
                key={cat.id || cat.name}
                type="button"
                className={`pos-category-pill btn btn-sm rounded fw-medium text-nowrap ${isActive ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(cat.id || cat.name);
                  setIsMoreOpen(false);
                }}
              >
                {cat.name}
              </button>
            );
          })}

          {overflowCategories.length > 0 && (
            <div
              className="pos-category-more-wrap position-relative d-inline-block"
              ref={moreDropdownRef}
            >
              <button
                type="button"
                className={`pos-category-more-btn btn btn-sm rounded fw-medium text-nowrap d-inline-flex align-items-center gap-1 ${
                  isOverflowSelected ? 'active-selected' : ''
                } ${isMoreOpen ? 'open' : ''}`}
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                aria-expanded={isMoreOpen}
              >
                <span>
                  {isOverflowSelected ? selectedOverflowCategory?.name : 'More'}
                </span>
                <FontAwesomeIcon
                  icon={isMoreOpen ? faChevronUp : faChevronDown}
                  className="fs-7 ms-1"
                />
              </button>

              {isMoreOpen && (
                <div className="pos-category-dropdown position-absolute top-100 start-0 bg-white border rounded shadow-sm p-1 list-unstyled m-0">
                  {overflowCategories.map((cat) => {
                    const isActive = selectedCategory === (cat.id || cat.name);
                    return (
                      <button
                        key={cat.id || cat.name}
                        type="button"
                        className={`pos-category-dropdown-item w-100 text-start py-2 px-3 border-0 bg-transparent rounded text-truncate ${
                          isActive ? 'active' : ''
                        }`}
                        onClick={() => {
                          setSelectedCategory(cat.id || cat.name);
                          setIsMoreOpen(false);
                        }}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Middle scrollable area for Products & Pagination */}
      <div className="pos-catalog-body flex-grow-1 overflow-auto pe-1">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading products...</span>
            </div>
          </div>
        ) : currentProducts.length > 0 ? (
          <div className="pos-product-grid mb-3">
            {currentProducts.map((product) => (
              <ProductCard
                key={product.id || product._id}
                product={product}
                isSelected={isSameProduct(selectedProduct, product)}
                onSelect={handleSelectProduct}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <p className="mb-1">
              {searchQuery ? (
                <span>
                  No products found matching &ldquo;
                  <strong>{searchQuery}</strong>&rdquo;
                </span>
              ) : (
                <span>No products available.</span>
              )}
            </p>
            <small>
              {searchQuery
                ? 'Try checking the spelling or searching with a different term.'
                : 'Please add products to your inventory.'}
            </small>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pos-pagination-container d-flex align-items-center justify-content-center gap-1 mt-1 mb-3">
            <button
              type="button"
              className="pos-page-btn arrow-btn btn btn-sm border rounded m-0"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              aria-label="Previous Page"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {getPageNumbers().map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`dots-${index}`}
                    className="pos-page-btn ellipsis d-inline-flex align-items-center justify-content-center"
                  >
                    ...
                  </span>
                );
              }
              const pageNum = Number(page);
              const isActive = currentPage === pageNum;
              return (
                <button
                  key={pageNum}
                  type="button"
                  className={`pos-page-btn btn btn-sm border rounded d-inline-flex align-items-center justify-content-center ${isActive ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              className="pos-page-btn arrow-btn btn btn-sm border rounded"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              aria-label="Next Page"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* Frequently Sold Bar fixed at Bottom */}
      {productsList.length > 0 && (
        <div className="pos-frequently-sold-section flex-shrink-0 mt-auto pt-2 border-top">
          <div className="pos-frequently-sold-title fw-bold text-dark small mb-2">
            <span>⭐</span> Frequently Sold
          </div>
          <div
            className="pos-frequently-sold-row"
            onWheel={(e) => {
              if (e.deltaY !== 0) {
                e.currentTarget.scrollLeft += e.deltaY;
              }
            }}
          >
            {(topSellingProducts.length > 0
              ? topSellingProducts
              : productsList.slice(0, 20)
            ).map((item) => (
              <FrequentlySoldChip
                key={item.id || item._id}
                item={item}
                isSelected={isSameProduct(selectedProduct, item)}
                onSelect={handleSelectProduct}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
